import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useTenant } from '@/contexts/TenantContext';
import { toast } from 'sonner';
import { Loader2, Building2, Lock, Mail, Sparkles, ShieldCheck, Zap, MessageSquare, BarChart3, Users, TrendingUp } from 'lucide-react';
import logoFull from '@/assets/fluxo-logo-full.png';
import './TenantLoginPage.css';

// Floating particles component — pure CSS + JS, zero deps
const FloatingParticles = ({ color }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];

    const resize = () => {
      if (!canvas) return;
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resize();
    window.addEventListener('resize', resize);

    // Create particles
    for (let i = 0; i < 65; i++) {
      particles.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        r: Math.random() * 2.5 + 1,
        dx: (Math.random() - 0.5) * 0.5,
        dy: (Math.random() - 0.5) * 0.5,
        opacity: Math.random() * 0.6 + 0.3, // Brighter particles
      });
    }

    const animate = () => {
      if (!canvas) return;
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      
      // Draw connections first
      particles.forEach((p1, i) => {
        for (let j = i + 1; j < particles.length; j++) {
            const p2 = particles[j];
            const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
            if (dist < 120) {
                const opacity = (1 - dist / 120) * 0.2;
                ctx.beginPath();
                ctx.moveTo(p1.x, p1.y);
                ctx.lineTo(p2.x, p2.y);
                ctx.strokeStyle = `${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
                ctx.lineWidth = 0.8;
                ctx.stroke();
            }
        }
      });

      // Draw particles on top
      particles.forEach(p => {
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.offsetWidth) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.offsetHeight) p.dy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 2);
        glow.addColorStop(0, `${color}${Math.round(p.opacity * 255).toString(16).padStart(2, '0')}`);
        glow.addColorStop(1, 'transparent');
        ctx.fillStyle = glow;
        ctx.fill();
      });
      animationId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, [color]);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 1 }}
    />
  );
};

const TenantLoginPage = () => {
  const { tenant, loading, loginTenant, tenantSlug } = useTenant();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    setIsLoggingIn(true);
    const result = await loginTenant(email, password);
    setIsLoggingIn(false);
    if (result.success) {
      toast.success(`¡Bienvenido a ${tenant?.name}!`);
      navigate(`/tenant/${tenantSlug}/dashboard`);
    } else {
      toast.error(result.error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#06080f]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#06080f] p-4 text-center">
        <div>
          <Building2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2 text-white">Tenant no encontrado</h2>
          <Button variant="outline" onClick={() => (window.location.href = '/')}>Ir al inicio</Button>
        </div>
      </div>
    );
  }

  const primaryColor = tenant.primary_color || '#06b6d4';
  const dynamicStyles = {
    '--tenant-primary': primaryColor,
    '--tenant-glow': `${primaryColor}22`,
    '--tenant-gradient': `linear-gradient(135deg, ${primaryColor}, ${primaryColor}aa)`
  };

  return (
    <div className="tenant-login-page" style={dynamicStyles}>
      {/* ======== LOGO / FORM SECTION ======== */}
      <div className="tl-form-section">
        <FloatingParticles color={primaryColor} />
        
        <div className="tl-form-card">
          {tenant.logo_url ? (
            <div className="tl-logo-full-container">
              <img src={tenant.logo_url} alt={tenant.name} className="tl-logo-main" />
              <div className="tl-tenant-info-mini">
                <span className="tl-tenant-name-small">{tenant.name}</span>
                <span className="tl-dot">•</span>
                <span className="tl-tenant-portal-tag">Portal Pro</span>
              </div>
            </div>
          ) : (
            <div className="tl-tenant-badge">
              <div className="tl-tenant-icon" style={{ background: 'var(--tenant-gradient)' }}>
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="tl-tenant-name">{tenant.name}</div>
                <div className="tl-tenant-subtitle">Portal de Gestión Directa</div>
              </div>
            </div>
          )}

          <h2 className="tl-heading">Acceso Empresarial</h2>
          <p className="tl-subheading">Gestiona tus flujos de WhatsApp y automatización para {tenant.name}</p>

          <form onSubmit={handleLogin}>
            <div className="tl-field">
              <label className="tl-label">Email Corporativo</label>
              <div className="tl-input-wrapper">
                <Mail className="tl-input-icon" />
                <input
                  type="email"
                  placeholder="ejemplo@empresa.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="tl-input"
                  required
                />
              </div>
            </div>

            <div className="tl-field">
              <div className="tl-password-header">
                <label className="tl-label">Contraseña</label>
                <button type="button" className="tl-forgot-link" style={{ color: primaryColor }}>Restablecer</button>
              </div>
              <div className="tl-input-wrapper">
                <Lock className="tl-input-icon" />
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="tl-input"
                  required
                />
              </div>
            </div>

            <button type="submit" className="tl-submit-btn" style={{ background: 'var(--tenant-gradient)' }} disabled={isLoggingIn}>
              {isLoggingIn ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Acceder al Portal'}
            </button>
          </form>

          <div className="tl-branding-footer">
            <span className="tl-powered-badge" style={{ color: `${primaryColor}aa` }}>Cloud Powered By</span>
            <img src={logoFull} alt="Fluxo Co" className="tl-powered-logo" />
            <span className="tl-copyright">© 2026 Fluxo Co Technology</span>
          </div>
        </div>
      </div>

      {/* ======== VISUAL SECTION (Desktop) ======== */}
      <div className="tl-visual-section">
        <div className="tl-mesh-bg" />
        <div className="tl-visual-aura" />
        <div className="tl-visual-content">
          <div className="tl-visual-tag">
            <Sparkles className="w-4 h-4" style={{ color: primaryColor }} />
            <span className="tl-visual-tag-text" style={{ color: primaryColor }}>Premium Experience</span>
          </div>
          <h2 className="tl-visual-headline">Impulsa tu<br /><span>Negocio</span></h2>
          <p className="tl-visual-desc">Transforma la manera en que te comunicas con tus clientes usando IA de última generación.</p>
          
          <div className="tl-features-row">
            <div className="tl-feature-pill"><Zap className="w-4 h-4" style={{ color: primaryColor }} /> Bots Ágiles</div>
            <div className="tl-feature-pill"><MessageSquare className="w-4 h-4" style={{ color: primaryColor }} /> IA Integrada</div>
            <div className="tl-feature-pill"><ShieldCheck className="w-4 h-4" style={{ color: primaryColor }} /> Seguridad</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantLoginPage;
